import {
  Button,
  Checkbox,
  IconButton,
  LoadingDots,
  Modal, Radio
} from 'components/core';
import { FC, useState } from 'react';
import { MdClose } from 'react-icons/md';
import {
  useCreateExportProjectJob,
  useDownloadAsset,
} from 'reactQuery/mutations';
import { DownloadAgreementProps, Styled } from '.';
import { formatDateToMonthYear } from 'utils';
import {
  useContentful,
  useDesignHuddleProject,
  useDoctorProfile,
} from 'reactQuery/queries';
import { MaterialTypeEnum } from 'types';
import DownloadConfirmationModal from './DownloadConfirmationModal';
import AskOrderToPrintModal from './AskOrderToPrintModal';
import { downloadFormatHandler } from 'components/DownloadAgreementModal/DownloadAgreementModal.helper';
import { useI18n } from 'contexts/i18n';

const DownloadAgreementModal: FC<DownloadAgreementProps> = ({
  product,
  projectId,
  onClose,
  closeEditor,
  source,
  isAddedToCart,
  isDownloaded,
  setIsDownloaded,
  isVideo,
  hasMultiplePages,
}) => {
  console.log('isAddedToCart', isAddedToCart);
  const { data: contentfulData } = useContentful();
  const { data: doctorProfile } = useDoctorProfile();
  const designHuddleProject = useDesignHuddleProject(
    doctorProfile?.userName,
    projectId ?? '',
    isVideo
  );

  const { brandGuidelines } = contentfulData?.globalContents || {};
  const brandGuidelineLink = doctorProfile
    ? brandGuidelines[doctorProfile?.locale]
    : brandGuidelines['en-US'];

  const refetchHistory = source === 'ProductPage';
  const downloadStaticAssetMutation = useDownloadAsset(refetchHistory);
  const downloadCustomizedAssetMutation = useCreateExportProjectJob(true);
  const isDownloading =
    downloadStaticAssetMutation.isLoading ||
    downloadCustomizedAssetMutation.isLoading;

  const [isAcceptedBrandGuildlines, setIsAcceptedBrandGuildlines] = 
    useState(false);
  const [isAcceptedUsageGuildlines, setIsAcceptedUsageGuildlines] = 
    useState(false);
  const [selectedFormat, setselectedFormat] = useState<'pdf' | 'jpeg'>('jpeg'); // Default to 'jpeg'

  const formattedAssetExpirationDate = formatDateToMonthYear(
    product.discontinueOn
  );

  const i18n = useI18n();
  const isOrderable =
    product.materialType === MaterialTypeEnum.physical ||
    product.materialType === MaterialTypeEnum.digital_physical;

  const downloadFormat = downloadFormatHandler(
    designHuddleProject.data?.mediaType.mediaTypeId,
    isVideo
  );

  const downloadStaticAsset = async () => {
    const response = await downloadStaticAssetMutation.mutateAsync(product.sku);
    performDownload(response.file_urls);
    onClose();
  };

  const downloadCustomizedAsset = async () => {
    const designHuddleprojectId = projectId ?? '';

    const formatToDownload = isVideo
      ? 'mp4' 
      : hasMultiplePages
      ? 'pdf'
      : selectedFormat || downloadFormatHandler(
        designHuddleProject.data?.mediaType.mediaTypeId,
        isVideo
      );

    const data = await downloadCustomizedAssetMutation.mutateAsync({
      clinid: doctorProfile?.userName,
      projectId: designHuddleprojectId,
      format: formatToDownload,
      sku: product.sku,
      shouldCreatDownloadHistory: true,
    });
    performDownload([data?.downloadUrl]);
    if (setIsDownloaded) {
      setIsDownloaded(true);
    } else {
      onClose();
    }
  };

  const performDownload = async (downloadUrls: string[]) => {
    for (const downloadUrl of downloadUrls) {
      await new Promise(resolve => {
        const a = document.createElement('a');
        if (typeof a.download === 'undefined') {
          window.location.href = downloadUrl;
        } else {
          a.href = downloadUrl;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(resolve, 1000);
        }
      });
    }
  };

  const agreementConfirmationHandler = () => {
    if (product.isCustomizable) {
      downloadCustomizedAsset();
    } else {
      downloadStaticAsset();
    }
  };

  return (
    <>
      <Modal>
        {!isDownloaded && (
          <>
            <Styled.AgreementContainer>
              {isDownloading && (
                <div>
                  <Styled.ModalTitle>{i18n['Download']}</Styled.ModalTitle>
                  <Styled.DownloadBody>
                    {i18n['Your file is being processed']}
                  </Styled.DownloadBody>
                  <br />
                  <LoadingDots />
                </div>
              )}
              {!isDownloading && (
                <>
                  <Styled.ModalTitle>
                    {i18n['Terms and conditions for free downloads']}
                    <IconButton onClick={onClose} showBorder={true} size="xs">
                      <MdClose />
                    </IconButton>
                  </Styled.ModalTitle>
                  {/* {!designHuddleProject.isLoading &&
                    product.isCustomizable &&
                    downloadFormat !== 'mp4' && 
                     !hasMultiplePages && ( */}
                     {product.isCustomizable && !isVideo && !hasMultiplePages && (
                     <Styled.Terms>
                       <Styled.FileFormatWrapper>
                          <p><strong>{i18n['Choose format']}:</strong></p> 
                              <Styled.FormatOptionItem onClick={() => setselectedFormat('jpeg')}>
                              <Radio
                                checked={selectedFormat === 'jpeg'}
                                onChange={() => setselectedFormat('jpeg')}
                               />
                             <span>JPEG</span>
                            </Styled.FormatOptionItem>      
                             <Styled.FormatOptionItem onClick={() => setselectedFormat('pdf')}>
                             <Radio
                               checked={selectedFormat === 'pdf'}
                               onChange={() => setselectedFormat('pdf')}
                             />
                             <span>PDF</span>
                            </Styled.FormatOptionItem>
                     
                      </Styled.FileFormatWrapper>
                    </Styled.Terms>
                   )}
                  <Styled.Terms>
                    <Checkbox
                      label=""
                      checked={isAcceptedBrandGuildlines}
                      onChange={() => {
                        setIsAcceptedBrandGuildlines(
                          !isAcceptedBrandGuildlines
                        );
                      }}
                    >
                      <Styled.TermsText>
                        {i18n['I acknowledge that I have read the']}{' '}
                        <Styled.Link href={brandGuidelineLink} target="_blank">
                          {i18n['Brand Guidelines']}
                        </Styled.Link>{' '}
                        {i18n['and I agree to comply with them.']}
                      </Styled.TermsText>
                    </Checkbox>
                  </Styled.Terms>
                  {formattedAssetExpirationDate && (
                    <Styled.Terms>
                      <Checkbox
                        label=""
                        checked={isAcceptedUsageGuildlines}
                        onChange={() => {
                          setIsAcceptedUsageGuildlines(
                            !isAcceptedUsageGuildlines
                          );
                        }}
                      >
                        <Styled.TermsText>
                          {i18n['I acknowledge that I will stop using this asset on']}{' '}
                          {formattedAssetExpirationDate}                  
                        </Styled.TermsText>
                      </Checkbox>
                    </Styled.Terms>
                  )}

                  <Styled.ButtonWrapper>
                    <Button
                      variant="primary"
                      disabled={
                        !(
                          isAcceptedBrandGuildlines &&
                          (isAcceptedUsageGuildlines ||
                            formattedAssetExpirationDate === '')
                        ) ||
                        downloadStaticAssetMutation.isLoading ||
                        designHuddleProject.isLoading
                      }
                      loading={
                        downloadStaticAssetMutation.isLoading ||
                        designHuddleProject.isLoading
                      }
                      onClick={agreementConfirmationHandler}
                      width="20%"
                    >
                      {i18n['Agree']}
                    </Button>
                  </Styled.ButtonWrapper>
                </>
              )}
            </Styled.AgreementContainer>
          </>
        )}
      </Modal>

      {((isDownloaded && !isOrderable) || (isDownloaded && isAddedToCart)) && (
        <DownloadConfirmationModal
          onClose={() => {
            setIsDownloaded && setIsDownloaded(false);
            onClose();
            closeEditor && closeEditor();
          }}
        />
      )}
      {isDownloaded && isOrderable && !isAddedToCart && (
        <AskOrderToPrintModal
          onClose={() => {
            setIsDownloaded && setIsDownloaded(false);
            onClose();
            closeEditor && closeEditor();
          }}
          onAddToCart={onClose}
        />
      )}
    </>
  );
};

export default DownloadAgreementModal;
